/**
 * Where the API key comes from. Today: an account API key typed by the user. Real login (users, roles,
 * Google sign-in) will provide a different source with the same shape; nothing else in the app knows
 * where the credential comes from.
 */
export interface CredentialSource {
  /** Current Authorization header value, or null when signed out. */
  authorization(): string | null;
  /** True when the credential survives closing the tab. */
  readonly remembered: boolean;
  signOut(): void;
  /** True for a signed-in browser session: requests carry the cookie, and `extraHeaders()` instead of an Authorization header. */
  readonly cookieSession?: boolean;
  extraHeaders?(): Record<string, string>;
}

/**
 * A signed-in person (cookie session). The cookie itself is HttpOnly and never visible to this code; all we hold, in memory
 * only, is the CSRF token and which account the person is working in.
 */
export class SessionCredentials implements CredentialSource {
  readonly cookieSession = true;
  readonly remembered = false;
  private csrf = "";
  private account: string | null = null;
  private active = false;

  authorization(): string | null {
    return null;
  }
  get signedIn() {
    return this.active;
  }
  set(csrf: string, accountId: string | null): void {
    this.csrf = csrf;
    this.account = accountId;
    this.active = true;
  }
  setAccount(accountId: string | null): void {
    this.account = accountId;
  }
  extraHeaders(): Record<string, string> {
    return { ...(this.csrf ? { "x-csrf-token": this.csrf } : {}), ...(this.account ? { "x-account-id": this.account } : {}) };
  }
  signOut(): void {
    this.csrf = "";
    this.account = null;
    this.active = false;
  }
}

const STORAGE_KEY = "nemo.settings.apiKey";

export class ApiKeyCredentials implements CredentialSource {
  private key: string | null;
  remembered = false;

  constructor(
    key: string | null = null,
    private readonly storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null = safeLocalStorage(),
  ) {
    this.key = key;
    if (!key && storage) {
      try {
        const saved = storage.getItem(STORAGE_KEY);
        if (saved) {
          this.key = saved;
          this.remembered = true;
        }
      } catch {
        /* storage blocked */
      }
    }
  }

  /** The key stays in memory. Only an explicit "remember on this device" writes it to storage. */
  signIn(key: string, remember: boolean): void {
    this.key = key.trim();
    this.remembered = remember;
    try {
      if (remember) this.storage?.setItem(STORAGE_KEY, this.key);
      else this.storage?.removeItem(STORAGE_KEY);
    } catch {
      this.remembered = false;
    }
  }

  authorization(): string | null {
    return this.key ? `Bearer ${this.key}` : null;
  }

  signOut(): void {
    this.key = null;
    this.remembered = false;
    try {
      this.storage?.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
